// =========================================================
// BlogCreate.jsx — 블로그 생성 (매물과 무관한 일반 콘텐츠용)
// BLOGPAGE_V1
//
// [현재 기능] 네이버 대표이미지(썸네일) 만들기
//   · 제목 직접 입력 (최대 4줄)
//   · 배경 사진 업로드, 없으면 단색 배경
//   · PNG 다운로드 (파일명: 날짜_제목.png)
//
// [나중에 추가할 것] 본문 AI 생성
//
// [관련] backend/routes/blogThumb.js 의 /api/blog-thumb/generate 를 호출한다.
//        매물 블로그(EstateEditor.jsx)와 같은 API를 쓴다.
// =========================================================

import React, { useState } from "react";
import { authFetch } from "../api/authFetch";

export default function BlogCreate() {
  const [title, setTitle]     = useState("");   // 제목 (줄바꿈 가능)
  const [bgB64, setBgB64]     = useState("");   // 업로드한 배경사진 (base64)
  const [bgName, setBgName]   = useState("");   // 업로드한 파일 이름 (표시용)
  const [result, setResult]   = useState(null); // { image, fileName, lines }
  const [loading, setLoading] = useState(false);

  // 사진 업로드 → base64 로 읽어 보관
  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setBgB64(String(reader.result));
      setBgName(f.name);
    };
    reader.readAsDataURL(f);
  };

  // 서버에 썸네일 생성 요청
  const generate = async () => {
    if (!title.trim()) { alert("제목을 입력해주세요."); return; }
    setLoading(true);
    try {
      const res = await authFetch("/api/blog-thumb/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          imageB64: bgB64,
          // estateNo 를 보내지 않으면 서버가 파일명에 날짜를 붙인다
        }),
      });
      const data = await res.json();
      if (data.ok) setResult(data);
      else alert("생성 실패: " + (data.error || "알 수 없는 오류"));
    } catch (e) {
      alert("생성 실패: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // 만들어진 이미지를 파일로 내려받기
  const download = () => {
    if (!result?.image) return;
    const a = document.createElement("a");
    a.href = result.image;
    a.download = result.fileName || "thumbnail.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "24px 20px 60px" }}>

      <h2 style={{ fontSize: "22px", marginBottom: "6px" }}>블로그 생성</h2>
      <p style={{ color: "#718096", fontSize: "14px", marginBottom: "26px" }}>
        네이버 블로그 목록에 표시될 대표이미지를 만듭니다.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "28px" }}>

        {/* ── 왼쪽: 입력 ── */}
        <div>
          <label style={{ display: "block", fontWeight: "bold",
                          marginBottom: "8px", fontSize: "15px" }}>
            제목{" "}
            <span style={{ fontWeight: "normal", color: "#718096", fontSize: "13px" }}>
              (Enter로 줄바꿈, 최대 4줄 · 한 줄 9자 내외가 보기 좋습니다)
            </span>
          </label>
          <textarea
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            rows={4}
            style={{ width: "100%", padding: "12px", fontSize: "17px",
                     border: "1px solid #cbd5e0", borderRadius: "8px",
                     lineHeight: "1.6", resize: "vertical", boxSizing: "border-box" }}
            placeholder={"예)\n하수구 날벌레\n차단 시공"}
          />

          <label style={{ display: "block", fontWeight: "bold",
                          margin: "24px 0 8px", fontSize: "15px" }}>
            배경 사진{" "}
            <span style={{ fontWeight: "normal", color: "#718096", fontSize: "13px" }}>
              (없으면 단색 배경으로 만들어집니다)
            </span>
          </label>

          <div style={{ display: "flex", gap: "10px", alignItems: "center",
                        flexWrap: "wrap" }}>
            <label style={{ padding: "10px 16px", border: "1px solid #cbd5e0",
                            borderRadius: "8px", cursor: "pointer", fontSize: "14px",
                            background: bgB64 ? "#ebf4ff" : "#fff" }}>
              사진 선택
              <input type="file" accept="image/*" onChange={onFilePick}
                     style={{ display: "none" }} />
            </label>

            {bgB64 && (
              <button
                onClick={() => { setBgB64(""); setBgName(""); }}
                style={{ padding: "10px 16px", border: "1px solid #cbd5e0",
                         borderRadius: "8px", cursor: "pointer",
                         fontSize: "14px", background: "#fff" }}>
                사진 제거
              </button>
            )}

            {bgName && (
              <span style={{ fontSize: "13px", color: "#718096" }}>{bgName}</span>
            )}
          </div>

          {/* 업로드한 사진 미리보기 */}
          {bgB64 && (
            <img src={bgB64} alt=""
                 style={{ width: "180px", height: "180px", objectFit: "cover",
                          borderRadius: "8px", marginTop: "14px", display: "block" }} />
          )}

          <button
            onClick={generate}
            disabled={loading}
            style={{ marginTop: "28px", width: "100%", padding: "14px",
                     borderRadius: "10px", border: "none", background: "#4c51bf",
                     color: "#fff", fontWeight: "bold", fontSize: "16px",
                     cursor: loading ? "wait" : "pointer",
                     opacity: loading ? 0.6 : 1 }}>
            {loading ? "만드는 중..." : "이미지 만들기"}
          </button>
        </div>

        {/* ── 오른쪽: 미리보기 ── */}
        <div>
          <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "15px" }}>
            미리보기
          </div>
          <div style={{ width: "100%", aspectRatio: "1 / 1", background: "#edf2f7",
                        borderRadius: "10px", display: "flex", alignItems: "center",
                        justifyContent: "center", overflow: "hidden" }}>
            {result?.image
              ? <img src={result.image} alt=""
                     style={{ width: "100%", display: "block" }} />
              : <span style={{ color: "#a0aec0", fontSize: "14px" }}>
                  [이미지 만들기]를 누르세요
                </span>}
          </div>

          {result && (
            <>
              <div style={{ fontSize: "13px", color: "#718096", margin: "10px 0" }}>
                저장될 이름: {result.fileName}
              </div>
              <button
                onClick={download}
                style={{ width: "100%", padding: "13px", borderRadius: "10px",
                         border: "none", background: "#2f855a", color: "#fff",
                         fontWeight: "bold", fontSize: "15px", cursor: "pointer" }}>
                PNG 다운로드
              </button>
              <div style={{ fontSize: "12px", color: "#718096", marginTop: "10px",
                            lineHeight: "1.6" }}>
                내려받은 이미지를 네이버 블로그 글의 <b>첫 번째 이미지</b>로 올리면
                목록에서 대표이미지로 표시됩니다.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
